import React, { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import SearchBar from "../components/SearchBar";
import RiverList from "../components/RiverList";
import FacilityList from "../components/FacilityList";
import SearchResults from "../components/SearchResults";
import "../css/homepage.css"

const createFacilityIcon = (hasAlert, hasNotification) => {
    return L.divIcon({
        className: "map-river-icon-wrap facility-water",
        html: `
        <div style="position: relative;">
            <div class="map-river-icon"></div>
            <div class="map-river-bagdes">
               ${hasAlert == 1 ? `<div class="map-river-alert"></div>` : ``}
                ${hasNotification == 1 ? `<div class="map-river-notification"></div>` : ``}
            </div>
          
        </div>
      `,
        iconSize: [40, 40],
        iconAnchor: [40, 40],
        popupAnchor: [0, -40],
    });
};

const createRiverIcon = (facilityCount, hasAlert, hasNotification) => {
    return L.divIcon({
        className: "map-river-icon-wrap custom-river-icon",
        html: `
            <div class="map-river-icon"></div>

            <div class="map-river-bagdes">
                ${hasAlert == 1 ? `<div class="map-river-alert"></div>` : ``}
                ${hasNotification == 1 ? `<div class="map-river-notification"></div>` : ``}
            </div>

            <div class="map-river-count">${facilityCount}</div>
        `,
        iconSize: [45, 45],
        iconAnchor: [40, 40],
        popupAnchor: [0, -53],
    });
};


// Helper function to check if coordinates are valid
const hasValidCoordinates = (facility) => {
    return facility.latitude &&
        facility.longitude &&
        !isNaN(parseFloat(facility.latitude)) &&
        !isNaN(parseFloat(facility.longitude));
};

// Helper function to calculate average coordinates
const calculateAverageCoordinates = (facilities) => {
    if (!facilities.length) return null;

    const avgLat = facilities.reduce((sum, f) => sum + Number(f.latitude), 0) / facilities.length;
    const avgLng = facilities.reduce((sum, f) => sum + Number(f.longitude), 0) / facilities.length;

    return { latitude: avgLat, longitude: avgLng };
};

const Mappage = ({ regionsData }) => {

    const getUrlParams = () => {
        const path = window.location.pathname;

        if (path === "/map" || path === "/map/") {
            window.history.replaceState({}, "", "/map");
            return { type: "region", value: "all" };
        }

        const searchMatch = path.match(/\/map\/search\/(.+)/);
        if (searchMatch) {
            return {
                type: "search",
                value: decodeURIComponent(searchMatch[1]),
            };
        }

        const riverMatch = path.match(/\/map\/([^/]+)\/([^/]+)\/?$/);
        if (riverMatch) {
            return {
                type: "river",
                region: decodeURIComponent(riverMatch[1]),
                river: decodeURIComponent(riverMatch[2]),
            };
        }

        const regionMatch = path.match(/\/map\/([^/]+)\/?$/);
        if (regionMatch && regionMatch[1] !== "search") {
            return {
                type: "region",
                value: decodeURIComponent(regionMatch[1]),
            };
        }

        return { type: null, value: "" };
    };

    const urlParams = getUrlParams();

    // Slightly offset coordinates so river markers never overlap
    const offsetLatLng = (lat, lng, seed) => {
        const offset = (seed % 10) * 0.02; // tweak 0.02 if needed
        return [
            lat + offset,
            lng - offset
        ];
    };


    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef([]);

    const [selectedRegion, setSelectedRegion] = useState(
        urlParams.type === "region"
            ? urlParams.value
            : urlParams.type === "river"
                ? urlParams.region
                : "__ALL_RIVERS__"
    );

    const [selectedRiver, setSelectedRiver] = useState(null);
    const [searchTerm, setSearchTerm] = useState(
        urlParams.type === "search" ? urlParams.value : ""
    );
    const [riverSlugFromUrl, setRiverSlugFromUrl] = useState(
        urlParams.type === "river" ? urlParams.river : null
    );

    // Memoized all rivers
    const allRivers = useMemo(() => {
        if (!regionsData) return [];
        return regionsData.flatMap(region =>
            (region.rivers || []).map(river => ({
                ...river,
                region_name: region.region_name,
                region_slug: region.region_slug,
                region_id: region.region_id,
            }))
        );
    }, [regionsData]);

    // Handle river slug from URL
    useEffect(() => {
        if (riverSlugFromUrl && allRivers.length) {
            const river = allRivers.find(r => r.river_slug === riverSlugFromUrl);
            if (river) {
                setSelectedRiver(river.river_id);
                setRiverSlugFromUrl(null);
            }
        }
    }, [riverSlugFromUrl, allRivers]);

    // Memoized facilities with proper structure
    const allFacilities = useMemo(() => {
        if (!regionsData || !Array.isArray(regionsData)) return [];

        return regionsData.flatMap(region =>
            (region.rivers || []).flatMap(river =>
                (river.facilities || []).map(facility => ({
                    ...facility,
                    _uid: `${region.region_id}-${river.river_id}-${facility.facility_id ?? Math.random()}`,
                    river_name: river.river_name,
                    river_id: river.river_id,
                    river_slug: river.river_slug,
                    region_name: region.region_name,
                    region_id: region.region_id,
                    region_slug: region.region_slug,
                }))
            )
        );
    }, [regionsData]);

    // Memoized facilities with valid coordinates
    const mapFacilities = useMemo(() => {
        return allFacilities.filter(hasValidCoordinates);
    }, [allFacilities]);

    // ---- SEARCH: Searchpage-aligned flat facility list ----
    const allFacilitiesRaw = useMemo(() => {
        if (!regionsData) return [];
        return regionsData.flatMap(region =>
            (region.rivers || []).flatMap(river => {
                if (river.facilities && river.facilities.length > 0) {
                    return river.facilities.map(facility => ({
                        facility_id: facility.id || facility.facility_id,
                        facility_name: facility.facility_name,
                        river_name: river.river_name,
                        region_name: region.region_name,
                        near_town: facility.near_town || "",
                        slug: facility.facility_slug,
                        post_id: facility.post_id,
                        label: [
                            facility.facility_name,
                            river.river_name,
                            facility.near_town
                        ].filter(Boolean).join(", "),
                        type: "facility",
                        data: {
                            ...facility,
                            river_name: river.river_name,
                            river_id: river.river_id,
                            river_slug: river.river_slug,
                            region_name: region.region_name,
                            region_slug: region.region_slug,
                        },
                    }));
                }
                return [];
            })
        );
    }, [regionsData]);

    const allFacilitiesForSearch = useMemo(() => {
        return [
            ...new Map(allFacilitiesRaw.map(item => [item.label, item])).values()
        ];
    }, [allFacilitiesRaw]);
    // --------------------------------------------------------

    // Filtered rivers based on region
    const filteredRivers = useMemo(() => {
        return selectedRegion === "all"
            ? allRivers
            : allRivers.filter(r => r.region_slug === selectedRegion);
    }, [selectedRegion, allRivers]);


    // Displayed facilities
    const displayedFacilities = useMemo(() => {
        if (!selectedRiver) return [];
        const river = filteredRivers.find(r => r.river_id === selectedRiver);
        return river?.facilities
            ? river.facilities.map(f => ({
                ...f,
                river_name: river.river_name,
                region_name: river.region_name,
            }))
            : [];
    }, [selectedRiver, filteredRivers]);


    const riverCenters = useMemo(() => {
        return allRivers
            .map(river => {
                // ✅ Filter facilities for this river
                const allRiverFacilities = allFacilities.filter(
                    f => f.river_id === river.river_id
                );
                
                if (!allRiverFacilities.length) return null;
                
                // ✅ Remove duplicate facilities by facility_id FIRST
                const uniqueFacilities = Array.from(
                    new Map(
                        allRiverFacilities.map(f => [f.facility_id, f])
                    ).values()
                );
                
                // ✅ Filter for valid coordinates from unique facilities
                const facilitiesWithCoords = uniqueFacilities.filter(hasValidCoordinates);
                
                if (!facilitiesWithCoords.length) return null;
                
                // ✅ NEW: If only 1 facility, use its exact coordinates without averaging
                let latitude, longitude;
                if (facilitiesWithCoords.length === 1) {
                    latitude = Number(facilitiesWithCoords[0].latitude);
                    longitude = Number(facilitiesWithCoords[0].longitude);
                } else {
                    const coords = calculateAverageCoordinates(facilitiesWithCoords);
                    if (!coords) return null;
                    latitude = coords.latitude;
                    longitude = coords.longitude;
                }
                
                // ✅ Check alerts/notifications on unique facilities
                const hasAlert = uniqueFacilities.some(f => f.has_alert || f.alert);
                const hasNotification = uniqueFacilities.some(f => f.has_notification || f.notification);
                
                return {
                    river_id: river.river_id,
                    river_name: river.river_name,
                    river_slug: river.river_slug,
                    region_name: river.region_name,
                    region_slug: river.region_slug,
                    latitude: latitude,
                    longitude: longitude,
                    facility_count: uniqueFacilities.length,
                    has_alert: hasAlert,
                    has_notification: hasNotification,
                    is_single_facility: facilitiesWithCoords.length === 1, // ✅ Track if single facility
                };
            })
            .filter(Boolean);
    }, [allRivers, allFacilities]);

    // Filtered river centers based on selected region
    const filteredRiverCenters = useMemo(() => {
        if (selectedRegion === "all") return riverCenters;
        return riverCenters.filter(river => river.region_slug === selectedRegion);
    }, [selectedRegion, riverCenters]);

    // ✅ Search results aligned with live Map search logic
    const searchResults = useMemo(() => {
        if (!searchTerm.trim()) return null;

        const searchLower = searchTerm.trim().toLowerCase();
        const riverPrefixMatches = allFacilitiesForSearch.filter(item =>
            item.river_name?.toLowerCase().startsWith(searchLower)
        );

        const facilityNameMatches = allFacilitiesForSearch.filter(item =>
            item.facility_name?.toLowerCase().includes(searchLower)
        );

        const fallbackMatches = allFacilitiesForSearch.filter(item => {
            return (
                item.region_name?.toLowerCase().includes(searchLower) ||
                item.river_name?.toLowerCase().includes(searchLower) ||
                item.near_town?.toLowerCase().includes(searchLower)
            );
        });

        const matchedFacilities = [
            ...riverPrefixMatches,
            ...facilityNameMatches,
            ...fallbackMatches
        ].filter((item, index, self) =>
            index === self.findIndex(other => other.label === item.label)
        );

        // Convert matched facilities into SearchResults-compatible format
        return matchedFacilities.map(item => ({
            type: "facility",
            id: item.facility_id,
            name: item.facility_name,
            region: item.region_name,
            river: item.river_name,
            label: item.label,
            data: item.data,
        }));
    }, [searchTerm, allFacilitiesForSearch, regionsData]);

    const handleSearchChange = e => {
        const value = e.target.value;
        setSearchTerm(value);
        setSelectedRiver(null);
        setSelectedRegion("all");

        window.history.pushState(
            {},
            "",
            value.trim()
                ? `/map/search/${encodeURIComponent(value)}`
                : "/map"
        );
    };

    // Initialize map
    useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current) return;

        const map = L.map(mapRef.current).setView([45.2538, -69.4455], 7);

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 19,
        }).addTo(map);

        mapInstanceRef.current = map;

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!mapInstanceRef.current) return;

        markersRef.current.forEach(m => m.remove());
        markersRef.current = [];

        const map = mapInstanceRef.current;
        const bounds = L.latLngBounds([]);

        if (!selectedRiver) {
            filteredRiverCenters.forEach(river => {

                // ✅ NEW: Don't apply offset if river has only 1 facility
                let lat, lng;
                if (river.is_single_facility) {
                    lat = river.latitude;
                    lng = river.longitude;
                } else {
                    [lat, lng] = offsetLatLng(
                        river.latitude,
                        river.longitude,
                        river.river_id
                    );
                }

                const marker = L.marker(
                    [lat, lng],
                    {
                        icon: createRiverIcon(
                            river.facility_count,
                            river.alert_count,
                            river.notification_count
                        ),
                    }
                ).addTo(map);

                const popupContent = `
                    <div class="hover-card river">
                        <div class="name">${river.river_name}</div>
                    </div>
                `;
                marker.bindTooltip(popupContent, {
                    direction: "right",
                    offset: [12, 0],
                    permanent: false,
                    opacity: 1,
                    className: "hover-tooltip"
                });
                marker.on("mouseover", () => marker.openPopup());
                marker.on("mouseout", () => marker.closePopup());

                marker.on("click", () => {
                    setSelectedRiver(river.river_id);
                    window.history.pushState(
                        {},
                        "",
                        `/map/${river.region_slug}/${river.river_slug}`
                    );
                });

                bounds.extend(marker.getLatLng());
                markersRef.current.push(marker);
            });
        } else {
            displayedFacilities
                .filter(hasValidCoordinates)
                .forEach(facility => {

                    const marker = L.marker(
                        [facility.latitude, facility.longitude],
                        {
                            icon: createFacilityIcon(
                                facility.alert_count || facility.alert_count,
                                facility.notification_count || facility.notification_count
                            ),
                        }
                    ).addTo(map);

                    const popupContent = `
                        <div class="hover-card facility">
                            <div class="name">${facility.facility_name}</div>
                           
                        </div>
                    `;
                    marker.bindTooltip(popupContent, {
                        direction: "right",
                        offset: [12, 0],
                        permanent: false,
                        opacity: 1,
                        className: "hover-tooltip"
                    });
                    marker.on("mouseover", () => marker.openPopup());
                    marker.on("mouseout", () => marker.closePopup());

                    marker.on("click", () => {
                        window.location.href = `/facility/${facility.facility_slug}`;
                    });

                    bounds.extend(marker.getLatLng());
                    markersRef.current.push(marker);
                });
        }

        if (bounds.isValid()) {
            map.fitBounds(bounds, { padding: [50, 50] });
        } else {
            map.setView([45.2538, -69.4455], 7);
        }
    }, [
        selectedRegion,
        selectedRiver,
        filteredRiverCenters,
        displayedFacilities
    ]);



    // Event handlers
    const handleRegionClick = regionSlug => {
        setSelectedRegion(regionSlug);
        setSelectedRiver(null);
        setSearchTerm("");
        window.history.pushState({}, "", regionSlug === "all" ? "/map" : `/map/${regionSlug}`);
    };

    const handleRiverClick = riverId => {
        setSelectedRiver(riverId);
        const river = filteredRivers.find(r => r.river_id === riverId);
        if (river) {
            window.history.pushState(
                {},
                "",
                `/map/${selectedRegion === "all" ? "all" : selectedRegion}/${river.river_slug}`
            );
        }
    };

    // Selected region name
    const selectedRegionName = useMemo(() => {
        if (selectedRegion === "all") return "All Regions";
        const region = regionsData?.find(r => r.region_slug === selectedRegion);
        return region ? region.region_name : "All Regions";
    }, [selectedRegion, regionsData]);

    // Selected river data
    const selectedRiverData = useMemo(() => {
        return filteredRivers.find(r => r.river_id === selectedRiver);
    }, [selectedRiver, filteredRivers]);

    const headerSearchEl = document.getElementById("header-search-root");

    return (
        <>
            {headerSearchEl && createPortal(
                <div className="search-input-wrapper">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search rivers and facilities..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                    <button className="searchbar-button"></button>
                </div>,
                headerSearchEl
            )}
            <div className="facilities-map-top">
                <div className="facilities-map-top-left">
                    {/* Information Tip */}
                    <div className="information-tip map-mobile">
                        <div className="info-icon">
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M12 17C12.2833 17 12.521 16.904 12.713 16.712C12.905 16.52 13.0007 16.2827 13 16V12C13 11.7167 12.904 11.4793 12.712 11.288C12.52 11.0967 12.2827 11.0007 12 11C11.7173 10.9993 11.48 11.0953 11.288 11.288C11.096 11.4807 11 11.718 11 12V16C11 16.2833 11.096 16.521 11.288 16.713C11.48 16.905 11.7173 17.0007 12 17ZM12 9C12.2833 9 12.521 8.904 12.713 8.712C12.905 8.52 13.0007 8.28267 13 8C12.9993 7.71733 12.9033 7.48 12.712 7.288C12.5207 7.096 12.2833 7 12 7C11.7167 7 11.4793 7.096 11.288 7.288C11.0967 7.48 11.0007 7.71733 11 8C10.9993 8.28267 11.0953 8.52033 11.288 8.713C11.4807 8.90567 11.718 9.00133 12 9ZM12 22C10.6167 22 9.31667 21.7373 8.1 21.212C6.88334 20.6867 5.825 19.9743 4.925 19.075C4.025 18.1757 3.31267 17.1173 2.788 15.9C2.26333 14.6827 2.00067 13.3827 2 12C1.99933 10.6173 2.262 9.31733 2.788 8.1C3.314 6.88267 4.02633 5.82433 4.925 4.925C5.82367 4.02567 6.882 3.31333 8.1 2.788C9.318 2.26267 10.618 2 12 2C13.382 2 14.682 2.26267 15.9 2.788C17.118 3.31333 18.1763 4.02567 19.075 4.925C19.9737 5.82433 20.6863 6.88267 21.213 8.1C21.7397 9.31733 22.002 10.6173 22 12C21.998 13.3827 21.7353 14.6827 21.212 15.9C20.6887 17.1173 19.9763 18.1757 19.075 19.075C18.1737 19.9743 17.1153 20.687 15.9 21.213C14.6847 21.739 13.3847 22.0013 12 22Z"
                                    fill="#3E7E23"
                                />
                            </svg>
                        </div>
                        <div className="info-text">
                            <p>
                                To ensure you have a safe and enjoyable experience on the water,
                                view water levels, flow rates and safety information related to
                                our hydropower facilities.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="facilities-map-top-right">
                    {/* Regions List */}
                    <div className="region-list">
                        <ul className="list-unstyled">
                            <li>
                                <a
                                    href="javascript:void(0)"
                                    className={selectedRegion === "all" ? "active" : ""}
                                    onClick={() => handleRegionClick("all")}
                                >
                                    All Regions
                                </a>
                            </li>
                            {regionsData?.map((region) => (
                                <li key={region.region_id}>
                                    <a
                                        href="javascript:void(0)"
                                        className={selectedRegion === region.region_slug ? "active" : ""}
                                        onClick={() => handleRegionClick(region.region_slug)}
                                        data-region-slug={region.region_slug}
                                    >
                                        {region.region_name}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
            <div className="facilities-map-bottom">
                <div className="facilities-map-bottom-left">
                    {searchResults ? (
                        <SearchResults
                            results={searchResults}
                            searchTerm={searchTerm}
                            onRiverClick={handleRiverClick}
                            facilityData={regionsData}
                        />
                    ) : !selectedRiver ? (
                        <>
                            <div className="title mobile">
                                <h1
                                    className={
                                        selectedRegion !== "all"
                                            ? "river-systems-title"
                                            : ""
                                    }
                                >
                                    {selectedRegion !== "all" && (
                                        <span
                                            className="river-back-button"
                                            onClick={() => handleRegionClick("all")}
                                            
                                        >
                                            {/* back icon */}
                                        </span>
                                    )}

                                    River systems{" "}
                                    {selectedRegion === "all"
                                        ? "(All)"
                                        : `(${selectedRegionName})`}
                                </h1>
                            </div>

                            <RiverList
                                rivers={filteredRivers}
                                onRiverClick={handleRiverClick}
                                selectedRegion={selectedRegion}
                            />
                        </>
                    ) : (
                        <>
                            <button
                                className="river-back-button"
                                onClick={() => {
                                    setSelectedRiver(null);
                                    window.history.pushState({}, "", selectedRegion === "all" ? "/map" : `/map/${selectedRegion}`);
                                }}
                            >
                                <h1>
                                    {selectedRiverData
                                        ? `${selectedRiverData.river_name}`
                                        : `River Systems (${selectedRegionName})`}
                                </h1>
                            </button>
                            {displayedFacilities.length ? (
                                <FacilityList facilityData={displayedFacilities} />
                            ) : (
                                <h4>No facilities found in this river.</h4>
                            )}
                        </>
                    )}
                </div>
                <div className="facilities-map-bottom-right">
                    {
                        !selectedRiver ? (
                            <div className="title ">
                              
                                    <h1
                                        className={
                                            selectedRegion !== "all"
                                                ? "river-systems-title"
                                                : ""
                                        }
                                    >
                                        {selectedRegion !== "all" && (
                                            <span
                                                className="river-back-button"
                                                onClick={() => handleRegionClick("all")}
                                                
                                            >
                                                {/* back icon */}
                                            </span>
                                        )}

                                        River systems{" "}
                                        {selectedRegion === "all"
                                            ? "(All)"
                                            : `(${selectedRegionName})`}
                                    </h1>
                               

                            </div>) : (
                                <button
                                className="river-back-button"
                                onClick={() => {
                                    setSelectedRiver(null);
                                    window.history.pushState({}, "", selectedRegion === "all" ? "/map" : `/map/${selectedRegion}`);
                                }}
                            >
                                <h1>
                                    {selectedRiverData
                                        ? `${selectedRiverData.river_name}`
                                        : `River Systems (${selectedRegionName})`}
                                </h1>
                            </button>)
                    }
                    {/* Right Side - Map always visible */}
                    <div className="search-main-right">
                        {/* Map Container - Always visible */}
                        <div ref={mapRef} className="map-container" />
                    </div>

                    {/* Information Tip */}
                    <div className="information-tip">
                        <div className="info-icon">
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M12 17C12.2833 17 12.521 16.904 12.713 16.712C12.905 16.52 13.0007 16.2827 13 16V12C13 11.7167 12.904 11.4793 12.712 11.288C12.52 11.0967 12.2827 11.0007 12 11C11.7173 10.9993 11.48 11.0953 11.288 11.288C11.096 11.4807 11 11.718 11 12V16C11 16.2833 11.096 16.521 11.288 16.713C11.48 16.905 11.7173 17.0007 12 17ZM12 9C12.2833 9 12.521 8.904 12.713 8.712C12.905 8.52 13.0007 8.28267 13 8C12.9993 7.71733 12.9033 7.48 12.712 7.288C12.5207 7.096 12.2833 7 12 7C11.7167 7 11.4793 7.096 11.288 7.288C11.0967 7.48 11.0007 7.71733 11 8C10.9993 8.28267 11.0953 8.52033 11.288 8.713C11.4807 8.90567 11.718 9.00133 12 9ZM12 22C10.6167 22 9.31667 21.7373 8.1 21.212C6.88334 20.6867 5.825 19.9743 4.925 19.075C4.025 18.1757 3.31267 17.1173 2.788 15.9C2.26333 14.6827 2.00067 13.3827 2 12C1.99933 10.6173 2.262 9.31733 2.788 8.1C3.314 6.88267 4.02633 5.82433 4.925 4.925C5.82367 4.02567 6.882 3.31333 8.1 2.788C9.318 2.26267 10.618 2 12 2C13.382 2 14.682 2.26267 15.9 2.788C17.118 3.31333 18.1763 4.02567 19.075 4.925C19.9737 5.82433 20.6863 6.88267 21.213 8.1C21.7397 9.31733 22.002 10.6173 22 12C21.998 13.3827 21.7353 14.6827 21.212 15.9C20.6887 17.1173 19.9763 18.1757 19.075 19.075C18.1737 19.9743 17.1153 20.687 15.9 21.213C14.6847 21.739 13.3847 22.0013 12 22Z"
                                    fill="#3E7E23"
                                />
                            </svg>
                        </div>
                        <div className="info-text">
                            <p>
                                To ensure you have a safe and enjoyable experience on the water,
                                view water levels, flow rates and safety information related to
                                our hydropower facilities.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Mappage;
